import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import moment from "moment";
import InventoryServices from "../../services/InventoryServices";
import { useDispatch, useSelector } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import { usePaginationState } from "../../hooks/usePaginationState";
import ConfirmDialog from "../cofirm/ConfirmDialog";
import translations from "../../assets/locals/translations";
const ModelBillings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const InventoryService = new InventoryServices();
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, handlePaginationModelChange] = usePaginationState('modelBillingsPaginationState', { pageSize: 50, page: 0 });
  const [dialogOpen, setDialogOpen] = useState({ modal: false, row: null });

  const theme = useTheme();
  // Adjust grid container based on screen size for responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Global search state
  const [searchQuery, setSearchQuery] = useState("");

  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];
  const fileInputRef = useRef(null);

  const getBillingLogs = async () => {
    setLoading(true);
    try {
      const response = await InventoryService.getBillingLogs();
      if (response && response.isSuccess) {
        const rowData = response.result.map((item, index) => ({
          ...item,
          bil: index + 1,
        }));
        setStockData(rowData);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBillingLogs();
  }, []);

  const editEnquiryCell = (row) => {
    navigate("/stock/billing/edit", { state: { data: row } });
  };

  const viewSteps = (row) => {
    navigate("/stock/billing/steps", { state: { data: row } });
  };

  const deleteActionCall = async (row) => {
    try {
      const response = await InventoryService.deleteStockLog(row.id);
      if (response && response.isSuccess) {
        dispatch(setShowMessage(response.message));
        await getBillingLogs();
      }
    } catch (error) {
      console.error(error, "Error getting servicing list");
    }
  };

  const openConfirmModal = (row) => {
    setDialogOpen({ modal: true, row });
  };

  const handleConfirm = async () => {
    if (dialogOpen.row) {
      await deleteActionCall(dialogOpen.row);
    }
    setDialogOpen({ modal: false, row: null });
  };

  const handleCancel = () => {
    setDialogOpen({ modal: false, row: null });
  };

  const columns = [
    { field: "bil", headerName: t.bil || "BIL", flex: 1, minWidth: 70 },
    { field: "bil_no", headerName: t.bilNo || "BIL NO", flex: 1, minWidth: 150 },
    {
      field: "date",
      headerName: (t.date || "Date").toUpperCase(),
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => moment(params.value).format("DD-MM-YYYY"),
    },
    { field: "bum_cod", headerName: t.bumCod || "BUM COD", flex: 1, minWidth: 100 },
    { field: "model_name", headerName: t.modal || t.model || "MODAL", flex: 1, minWidth: 100 },
    { field: "chassis", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    { field: "ndp", headerName: t.ndp || "NDP", flex: 1, minWidth: 120 },
    { field: "location", headerName: t.location || "Location", flex: 1, minWidth: 100 },
    { field: "number22", headerName: "22 Number", flex: 1, minWidth: 120 },
    { field: "stockStatus", headerName: "Stock Status", flex: 1, minWidth: 120 },
    {
      field: "menu",
      headerName: t.menu || "Menu",
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      disableColumnMenu: true,
      width: 100,
      renderCell: (params) => (
        <div className="d-flex justify-content-center">
          <div className="">
            <Tooltip title={t.edit || "Edit"}>
              <button
                className="myActionBtn m-1"
                onClick={() => editEnquiryCell(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-pencil-square"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path
                    fillRule="evenodd"
                    d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                  />
                </svg>
              </button>
            </Tooltip>
            <Tooltip title={t.delete || "Delete"}>
              <button
                onClick={() => openConfirmModal(params.row)}
                className="myActionBtn m-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-trash3"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  const handlePageSizeChange = (newPageSize) => {
    handlePaginationModelChange({ ...paginationModel, pageSize: newPageSize });
  };

  // Filter rows based on search query across multiple fields
  const filteredRows = useMemo(() => {
    if (!searchQuery) return stockData;
    return stockData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, stockData]);
  const handleSampleDownload = async () => {
    try {
      const response = await InventoryService.downloadSampleBilling();
      if (response && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Billing_Sample.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading sample:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await InventoryService.uploadDBillingExcel(formData);
      if (response && response.isSuccess) {
        dispatch(setShowMessage("Upload successful!"));
        await getBillingLogs();
      } else {
        dispatch(setShowMessage(response?.message || "Upload failed"));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setShowMessage("Error uploading file"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="bg-white p-2">
      {/* Header: search left, all action buttons right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300, flexShrink: 0 }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            marginLeft: isMobile ? 0 : "auto",
          }}
        >
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => navigate(`/stock/billing/add`)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-plus-circle me-2"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
            </svg>
            {t.addRecord || t.add || "Add"}
          </button>
          <button
            className="btn btn-outline-info d-flex align-items-center"
            onClick={handleSampleDownload}
            title="Download Sample Billing Excel"
          >
            Sample
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
          <button
            className="btn btn-outline-success d-flex align-items-center"
            onClick={() => fileInputRef.current?.click()}
            title="Upload Excel File"
          >
            Upload
          </button>
        </Box>
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.bil}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 15, 25, 50]}
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          disableSelectionOnClick
          autoHeight={isMobile}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{ width: "100%" }}
          loading={loading}
        />
      </div>

      <ConfirmDialog
        open={dialogOpen.modal}
        title={t.deleteconfirmation || "Delete Confirmation"}
        content={t.areyousuredelete || "Are you sure you want to delete this item? This action cannot be undone."}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText={t.delete || "Delete"}
        cancelText={t.cancel || "Cancel"}
      />
    </div>
  );
};

export default ModelBillings;
